/**
 * @file important-documents.js
 * @fileoverview Renders the important documents page.
 * @namespace important-documents
 * @description The Important Documents page.
 * @module common/assets/pages/Page/important-documents
 * @namespace important-documents
 * @since 3.0.0
 * @author jsaldivar
 */

import { i18n, loading, outputDiv } from "commonAssets/js/constants";
import { createElement } from "commonAssets/lib/createElement.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";

/**
 * Renders the important documents page.
 *
 * @param {Object} params - The parameters for rendering the page.
 * @param {string} params.slug - The slug of the page.
 * @param {Array} params.pages - The array of pages.
 */
export const importantDocumentsPage = (params) => {
    console.log("[Important Documents] ...");
    // define the constants
    const slug = params.slug;
    const page = params.pages.find((page) => page.PageName === slug);
    const pageTitle = createElement(
        "h2",
        { class: "page-title mt-2 mb-4" },
        [],
        page.PageTitle
    );
    const pageContent = createElement("div", { class: "page-content row" });
    const pageDiv = createElement(
        "div",
        { class: "page-div important-documents-container" },
        [pageTitle, pageContent]
    );
    const divImptDates = createElement(
        "div",
        {
            class: "my-5 mx-auto",
            id: "ImportantDates",
            style: "max-width: 512px;",
        },
        [],
        params.dates.card()
    );
    const pageColumns = createElement("div", { class: "col-12 col-md-8" });
    // documents column
    if (page.PageContent.length > 0) {
        const docList = createElement("ul", {
            class: "list-unstyled doc-list",
        });
        pageColumns.appendChild(docList);
        pageContent.appendChild(pageColumns);
        renderDocs(page, docList);
    } else {
        const noDocs = createElement(
            "p",
            { class: "fst-italic" },
            [],
            `No documents found.`
        );
        pageColumns.appendChild(noDocs);
        pageContent.appendChild(pageColumns);
    }
    // remove the my-5 and mx-auto classes from the divImptDates element
    if (divImptDates.classList.contains("my-5", "mx-auto")) {
        divImptDates.classList.remove("my-5", "mx-auto");
    }
    // important dates column
    const imptDatesColumn = createElement("div", { class: "col-12 col-md-4" }, [
        divImptDates,
    ]);
    pageContent.appendChild(imptDatesColumn);
    // output to DOM
    outputDiv.appendChild(pageDiv);
    loadingGif(loading, "hide", 100);
};

/**
 * Renders the documents on the page.
 *
 * @param {Object} page - The page object.
 * @param {HTMLElement} docList - The list element to append the documents to.
 * @returns {void}
 */
export function renderDocs(page, docList) {
    let listCount = 0;
    page.PageContent.forEach((element) => {
        listCount++;
        const docItem = createElement("li", { class: "doc-item mb-2" }, [
            createElement(
                "a",
                {
                    class: "text-decoration-none fs-5 text-dark mb-3 doc-link",
                    id: slugify(element.DocTitle),
                    href: element.DocURL,
                    target: "_blank",
                    rel: "noopener noreferrer",
                },
                [],
                `<i class="bi bi-file-earmark-pdf text-danger me-2"></i>` +
                    element.DocTitle
            ),
        ]);
        docList.appendChild(docItem);
    });
}

/**
 * Converts a string to a slug by replacing spaces with hyphens and removing non-word characters.
 * @param {string} str - The string to be slugified.
 * @returns {string} The slugified string.
 */
export function slugify(str) {
    return str
        .toLowerCase()
        .replace(/ /g, "-") // Replace spaces with hyphens
        .replace(/[^\w-]+/g, ""); // Remove non-word characters
}
